import React, { useEffect, useState, useRef } from 'react';
import { Button } from './ui/Button';
import { Play, Pause, Volume2 } from 'lucide-react';
export function HeroSection() {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMetadata = () => setDuration(audio.duration);
    const onEnded = () => setIsPlaying(false);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('ended', onEnded);
    };
  }, []);
  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };
  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = x / rect.width;
    audio.currentTime = pct * duration;
  };
  const formatTime = (t: number) => {
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  const progress = duration ? currentTime / duration * 100 : 0;
  return <section className="relative w-full min-h-[80vh] flex items-center justify-center text-center px-4 py-20 overflow-hidden" data-id="element-60">
      {/* Background Image */}
      <div className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat" style={{
      backgroundImage: 'url(https://images.unsplash.com/photo-1518199266791-5375a83190b7?w=1600)'
    }} data-id="element-61" />

      {/* Dark Overlay */}
      <div className="absolute inset-0 z-10 bg-gradient-to-b from-black/70 via-rose-950/60 to-black/70" data-id="element-62" />

      {/* Content */}
      <div className="relative z-20 max-w-4xl mx-auto text-white space-y-8" data-id="element-63">
        <div className="inline-block px-4 py-2 bg-rose-500/20 backdrop-blur-sm rounded-full border border-rose-300/30 mb-4" data-id="element-64">
          <span className="text-sm md:text-base font-medium tracking-wide" data-id="element-65">
            👉 Consulta privada y confidencial
          </span>
        </div>

        <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight tracking-tight" data-id="element-66">
          RECUPERA A ESA
          <br data-id="element-67" />
          <span className="text-rose-300" data-id="element-68">PERSONA ESPECIAL</span>
        </h1>

        <p className="text-lg md:text-xl font-medium tracking-widest uppercase text-gray-200" data-id="element-69">
          SIN HUMILLARTE · SIN ROGAR · SIN PERDER TU DIGNIDAD
        </p>

        <p className="text-lg md:text-2xl text-gray-100 max-w-2xl mx-auto leading-relaxed" data-id="element-70">
          Sanación espiritual profunda para restaurar la conexión, desbloquear
          energías y volver a unir caminos cuando el amor parece haberse
          enfriado.
        </p>

        <div className="pt-8" data-id="element-71">
          <a href="https://wa.me/13052196902" target="_blank" rel="noopener noreferrer" className="inline-block w-full sm:w-auto" data-id="element-72">
            <Button size="large" className="w-full sm:w-auto text-lg px-8 py-6 bg-rose-600 hover:bg-rose-700 text-white border-none font-bold shadow-[0_0_20px_rgba(225,29,72,0.5)] transition-all hover:scale-105" data-id="element-73">
              QUIERO RECUPERAR MI PAREJA
            </Button>
          </a>
        </div>

        {/* Audio Player */}
        <div className="pt-4 max-w-md mx-auto" data-id="element-74">
          <p className="text-xl md:text-2xl font-extrabold text-white mb-4 animate-pulse drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]" data-id="element-75">
            🎧 ¡ESCUCHA NUESTRO MENSAJE! 👇
          </p>
        </div>

        <div className="max-w-md mx-auto" data-id="element-76">
          <audio ref={audioRef} src="https://pikaso.cdnpk.net/private/production/3241934536/audio.mp3?token=exp=1770595200~hmac=8988dd3d20859417bee082ac4326e250919cb902714f1f9a8d51606747afa898" preload="metadata" data-id="element-77" />
          <div className="flex items-center gap-3 bg-white/10 backdrop-blur-md rounded-full px-4 py-3 border border-rose-300/20" data-id="element-78">
            {/* Play/Pause Button */}
            <button onClick={togglePlay} className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-rose-600 hover:bg-rose-700 transition-colors" aria-label={isPlaying ? 'Pausar' : 'Reproducir'} data-id="element-79">
              {isPlaying ? <Pause className="w-4 h-4 text-white fill-current" data-id="element-80" /> : <Play className="w-4 h-4 text-white fill-current ml-0.5" data-id="element-81" />}
            </button>

            {/* Progress Bar */}
            <div className="flex-grow h-2 bg-white/20 rounded-full cursor-pointer relative overflow-hidden" onClick={handleSeek} data-id="element-82">
              <div className="absolute inset-y-0 left-0 bg-rose-400 rounded-full transition-all duration-150" style={{
              width: `${progress}%`
            }} data-id="element-83" />
            </div>

            {/* Time */}
            <span className="text-xs text-rose-200 font-mono flex-shrink-0 w-[70px] text-right" data-id="element-84">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>

            {/* Volume Icon */}
            <Volume2 className="w-4 h-4 text-rose-300 flex-shrink-0" data-id="element-85" />
          </div>
        </div>

        <div className="pt-4 max-w-md mx-auto" data-id="element-86">
          
        </div>
      </div>
    </section>;
}